import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Shield,
  Truck,
  MapPin,
  Zap,
  PawPrint,
  Check,
  X,
  Clock,
  Car,
  Globe,
  Calendar,
  FileText,
  HeadphonesIcon,
  Smartphone,
  Users,
  MessageCircle,
  Heart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import SEO from "@/components/SEO";

const HERO_IMG = "/brand/hero-dark.png";
const SITE_URL = process.env.REACT_APP_BACKEND_URL || "https://uktranspet.com";

export default function Home() {
  const [popularBlogs, setPopularBlogs] = useState([]);

  useEffect(() => {
    api.get("/blogs").then(({ data }) => {
      // Prefer the top cross-channel routes (Folkestone-Calais, London-Paris, Calais-Folkestone, etc.)
      const preferred = [
        "pet-transport-folkestone-to-calais",
        "pet-transport-london-to-paris",
        "pet-transport-calais-to-folkestone",
        "pet-transport-amsterdam-to-london",
      ];
      const map = Object.fromEntries(data.map((b) => [b.slug, b]));
      setPopularBlogs(preferred.map((s) => map[s]).filter(Boolean));
    }).catch(() => {});
  }, []);

  const jsonLd = useMemo(() => ([
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "UK Pet Transport",
      url: SITE_URL,
      logo: `${SITE_URL}/brand/apple-touch-icon.png`,
      description: "DEFRA-approved pet chauffeur service from the UK to Europe. You and your pet travel together, door-to-door via Eurotunnel.",
      areaServed: ["United Kingdom", "European Union"],
      contactPoint: [{ "@type": "ContactPoint", contactType: "Customer Service", email: "uktranspet@gmail.com", availableLanguage: ["English"] }],
    },
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      name: "UK Pet Transport",
      url: SITE_URL,
      image: `${SITE_URL}/brand/apple-touch-icon.png`,
      priceRange: "££",
      areaServed: ["GB", "FR", "BE", "NL", "DE", "ES", "PT", "IT", "AT", "CH", "CZ"],
      serviceType: ["Pet transport", "Pet taxi", "Pet courier", "Pet relocation", "Eurotunnel pet travel"],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        { "@type": "Question", name: "How much is a pet taxi from London to Paris?", acceptedAnswer: { "@type": "Answer", text: "Our instant quote calculator gives you the exact price in under 60 seconds. London to Paris pet transport starts at £826 for a Volkswagen Passat, excluding the Eurotunnel LeShuttle Pet crossing fee (approximately £130–£250)." } },
        { "@type": "Question", name: "Do you transport cats and ferrets as well as dogs?", acceptedAnswer: { "@type": "Answer", text: "Yes — dogs, cats, and ferrets are all transported under the standard EU Pet Passport / GB Animal Health Certificate rules. Rabbits and small pets can also be arranged with slightly different paperwork." } },
        { "@type": "Question", name: "Is this a pet taxi or air cargo?", acceptedAnswer: { "@type": "Answer", text: "This is a professional pet taxi service — your pet travels in a climate-controlled Mercedes or Volkswagen Passat with a certified handler, door-to-door. No cages, no cargo hold, no Animal Reception Centre layovers." } },
        { "@type": "Question", name: "Can I travel with my pet in the same vehicle?", acceptedAnswer: { "@type": "Answer", text: "Yes, passengers are welcome in all vehicles at no extra charge, subject to seat count (Passat/E-Class: 1–3; Mercedes V-Class: up to 6). You and your pet door-to-door." } },
        { "@type": "Question", name: "How quickly do I get a quote?", acceptedAnswer: { "@type": "Answer", text: "Immediately — our online calculator at /quote returns the exact driving distance and price for all three vehicles in under 60 seconds. No form, no email required, no callbacks." } },
        { "@type": "Question", name: "Are you DEFRA approved?", acceptedAnswer: { "@type": "Answer", text: "Yes. We hold DEFRA Type 2 transporter authorisation, the regulatory tier required for commercial cross-border pet transport in the EU." } },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Pet Transport",
      provider: { "@type": "Organization", name: "UK Pet Transport" },
      areaServed: { "@type": "Place", name: "United Kingdom and European Union" },
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "GBP",
        lowPrice: "240",
        highPrice: "3500",
        description: "Door-to-door pet transport from the UK to Europe via Eurotunnel.",
      },
    },
  ]), []);

  return (
    <div>
      <SEO
        title="Pet Chauffeur UK to Europe — Door-to-Door Pet Taxi"
        description="Personal pet chauffeur service UK to Europe. Travel with your pet door-to-door via Eurotunnel. DEFRA approved pet taxi. Dogs, cats & ferrets. Free instant quote."
        path="/"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Dog and cat together" className="w-full h-full object-cover hero-photo" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/60 to-background/10"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/10 to-transparent"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl text-center md:text-left mx-auto md:mx-0"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-[0.2em] bg-background/60 text-foreground border border-border backdrop-blur">
              <PawPrint size={12} /> UK ↔ Europe · Door-to-door
            </span>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] font-medium mt-6 drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)]">
              Travel with your pet,<br /> UK to Europe.
            </h1>
            <p className="text-base sm:text-lg text-foreground/90 mt-6 max-w-xl mx-auto md:mx-0 leading-relaxed drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)]">
              Personal pet chauffeur service — you and your pet travel together, door-to-door via Eurotunnel.
              DEFRA approved. Dogs, cats &amp; ferrets.
            </p>
            <div className="mt-10 flex flex-wrap gap-4 justify-center md:justify-start">
              <Button asChild size="lg" className="h-12 px-8 text-base shadow-xl shadow-black/40" data-testid="hero-quote-btn">
                <Link to="/quote">Get instant quote <ArrowRight size={16} className="ml-2" /></Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-12 px-8 text-base bg-background/40 backdrop-blur border-foreground/30 hover:bg-background/70" data-testid="hero-book-btn">
                <Link to="/book">Register &amp; book</Link>
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap gap-4 justify-center md:justify-start text-xs text-foreground/90">
              <span className="inline-flex items-center gap-2"><Shield size={14} /> DEFRA Approved</span>
              <span className="inline-flex items-center gap-2"><Heart size={14} /> Owner travels with pet</span>
              <span className="inline-flex items-center gap-2"><MapPin size={14} /> Door-to-door</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* STOP WASTING TIME — instant-quote promo */}
      <section className="bg-secondary/40 border-y border-border" id="instant-quote-promo">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <span className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Stop wasting time</span>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-tight">
            Tired of filling long forms just to get a pet transport price?
          </h2>
          <p className="text-muted-foreground mt-6 text-lg max-w-3xl leading-relaxed">
            Most pet transport companies make you fill out long forms, wait for callbacks, or chase emails for days
            just to find out how much it costs. <strong className="text-foreground">We don't.</strong>
          </p>
          <p className="text-muted-foreground mt-4 text-lg max-w-3xl leading-relaxed">
            Our booking system gives you a guaranteed price in under 60 seconds. Enter your pickup, enter your
            destination — done. No waiting, no emails, no callbacks.
          </p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-card border border-border">
              <Zap className="text-primary shrink-0" size={20} strokeWidth={1.5} />
              <span className="text-sm">Instant price calculator</span>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-card border border-border">
              <Check className="text-primary shrink-0" size={20} strokeWidth={1.5} />
              <span className="text-sm">No account required</span>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-card border border-border">
              <Clock className="text-primary shrink-0" size={20} strokeWidth={1.5} />
              <span className="text-sm">Under 60 seconds</span>
            </div>
          </div>

          <div className="mt-10">
            <Button asChild size="lg" className="h-12 px-8" data-testid="stop-wasting-cta">
              <Link to="/quote">Get your guaranteed price now <ArrowRight size={16} className="ml-2" /></Link>
            </Button>
            <p className="text-xs text-muted-foreground mt-4 max-w-xl">
              Please note: quote does not include the Eurotunnel crossing ticket (purchased separately by the customer).
            </p>
          </div>
        </div>
      </section>

      {/* POPULAR ROUTES — text-only cards */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Popular routes</span>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-3">UK ↔ France · most booked.</h2>
          </div>
          <Button asChild variant="outline" data-testid="home-view-all-blogs">
            <Link to="/routes">All 60 routes <ArrowRight size={14} className="ml-2" /></Link>
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {popularBlogs.map((b) => (
            <Card
              key={b.slug}
              className="p-6 border-border bg-card hover:border-accent/60 transition flex flex-col"
              data-testid={`home-route-${b.slug}`}
            >
              <span className="text-[10px] uppercase tracking-[0.2em] text-accent font-semibold">
                {b.category === "International" ? (b.country || "Europe") : "United Kingdom"}
              </span>
              <h3 className="font-display text-xl mt-3 leading-snug">
                {b.route_from} <span className="text-muted-foreground">↔</span> {b.route_to}
              </h3>
              <p className="text-sm text-muted-foreground mt-2">{b.distance_miles} miles</p>
              <p className="mt-5">
                <span className="text-xs text-muted-foreground">From</span>{" "}
                <span className="font-display text-3xl">£{b.price_passat}</span>
              </p>
              <div className="mt-5 pt-5 border-t border-border/60 flex items-center justify-between gap-2">
                <Link to={`/blog/${b.slug}`} className="text-xs text-muted-foreground hover:text-foreground">
                  Route guide
                </Link>
                <Button asChild size="sm" data-testid={`home-book-${b.slug}`}>
                  <Link
                    to={`/book?pickup=${encodeURIComponent(b.route_from || "")}&dropoff=${encodeURIComponent(b.route_to || "")}&miles=${b.distance_miles || ""}${b.category === "International" ? "&channel=1" : ""}`}
                  >
                    Book <ArrowRight size={12} className="ml-1" />
                  </Link>
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* TAKE YOUR PET TO EUROPE — bullet list */}
      <section className="bg-secondary/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-7">
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Our service</span>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-3 leading-tight">
                Take your pet to France &amp; Europe.
              </h2>
              <p className="text-muted-foreground mt-6 max-w-2xl leading-relaxed">
                Travel with your dog or cat to Europe the stress-free way. As a DEFRA Type 2 authorised transporter,
                we offer a personal chauffeur service where you accompany your pet throughout the entire journey —
                from your doorstep to your destination.
              </p>
              <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  "You travel with your pet, not separately",
                  "Door-to-door collection and delivery",
                  "Climate-controlled comfortable vehicles",
                  "All motorway tolls included",
                  "Pet passport documentation assistance",
                  "Experienced, caring drivers",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm">
                    <span className="inline-flex h-6 w-6 rounded-full bg-primary/20 text-primary items-center justify-center shrink-0 mt-0.5">
                      <Check size={14} />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-5">
              <Card className="p-8 border-border bg-card">
                <span className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Personal pet taxi</span>
                <h3 className="font-display text-2xl mt-3 leading-snug">
                  Travel with your pet, not without them.
                </h3>
                <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
                  We're not a commercial pet shipping company. We're professional pet taxi drivers who take you and your
                  pet together on your journey. No cargo holds, no strangers — just a caring chauffeur service door-to-door.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {["Travel with dog to Europe", "Travel with cat to France", "Pet-friendly relocation"].map((t) => (
                    <span key={t} className="text-[11px] uppercase tracking-wider px-3 py-1 rounded-full border border-border bg-secondary/50">
                      {t}
                    </span>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US — 4 feature grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Why choose us</span>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-3">UK to France specialists.</h2>
          <p className="text-muted-foreground mt-6 leading-relaxed">
            Trusted transport between the UK and France via Eurotunnel. Years of experience moving dogs, cats, and
            ferrets safely through the Channel Tunnel.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {[
            {
              icon: Truck,
              title: "Channel Tunnel experts",
              body: "Specialist pet transport through Eurotunnel from any UK location to Europe and back. Experienced drivers who know the routes and regulations.",
            },
            {
              icon: FileText,
              title: "Pet passport compliant",
              body: "We ensure all documentation for cross-border pet travel is met. Guidance through pet passport, microchip, and vaccination requirements.",
            },
            {
              icon: MapPin,
              title: "Door-to-door",
              body: "Pickup anywhere in the UK with delivery across Europe including France, Germany, Spain, Netherlands, Belgium and more.",
            },
            {
              icon: Calendar,
              title: "Flexible scheduling",
              body: "Book to suit your travel plans with early morning, daytime, and evening crossings available.",
            },
          ].map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
            >
              <Card className="p-7 h-full border-border bg-card hover:border-accent/60 transition">
                <s.icon className="text-primary" size={26} strokeWidth={1.5} />
                <h3 className="font-display text-lg mt-5 font-medium">{s.title}</h3>
                <p className="text-muted-foreground text-sm mt-3 leading-relaxed">{s.body}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      {/* COMPARISON — us vs others */}
      <section className="bg-secondary/40 border-y border-border">
        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Industry-leading</span>
            <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-tight">
              The most advanced pet transport booking system in the UK.
            </h2>
            <p className="text-muted-foreground mt-6 leading-relaxed">
              While other companies make you fill out forms and wait for callbacks, we give you instant prices and
              let you book in minutes. No games, no waiting, no surprises.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16">
            <Card className="p-8 border-primary/40 bg-card">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 rounded-full bg-primary text-primary-foreground items-center justify-center"><Zap size={16} /></span>
                <h3 className="font-display text-xl">Our booking system</h3>
              </div>
              <ol className="mt-6 space-y-3 text-sm">
                <li className="flex gap-3"><span className="font-mono text-primary">1</span><span>Enter pickup &amp; drop-off locations</span></li>
                <li className="flex gap-3"><span className="font-mono text-primary">2</span><span>See instant price (no waiting)</span></li>
                <li className="flex gap-3"><span className="font-mono text-primary">3</span><span>Add your details &amp; pet info</span></li>
                <li className="flex gap-3"><span className="font-mono text-primary">4</span><span>Confirm booking — done</span></li>
              </ol>
              <p className="mt-6 text-xs uppercase tracking-wider text-primary font-semibold">Total time: under 2 minutes</p>
            </Card>

            <Card className="p-8 border-border bg-card/60">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-9 w-9 rounded-full bg-muted text-muted-foreground items-center justify-center"><X size={16} /></span>
                <h3 className="font-display text-xl text-muted-foreground">How others work</h3>
              </div>
              <ol className="mt-6 space-y-3 text-sm text-muted-foreground">
                <li className="flex gap-3"><span className="font-mono">1</span><span>Fill out long enquiry form</span></li>
                <li className="flex gap-3"><span className="font-mono">2</span><span>Wait 24–48 hours for email quote</span></li>
                <li className="flex gap-3"><span className="font-mono">3</span><span>Call back during office hours</span></li>
                <li className="flex gap-3"><span className="font-mono">4</span><span>Negotiate price (hidden fees appear)</span></li>
                <li className="flex gap-3"><span className="font-mono">5</span><span>Finally book… maybe</span></li>
              </ol>
              <p className="mt-6 text-xs uppercase tracking-wider font-semibold text-muted-foreground">Total time: 2–5 days</p>
            </Card>
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {[
              { icon: Zap, label: "Instant price calculator" },
              { icon: Globe, label: "Live route calculation" },
              { icon: Smartphone, label: "Mobile-first design" },
              { icon: MessageCircle, label: "WhatsApp support" },
              { icon: Users, label: "Multi-pet &amp; passenger bookings" },
              { icon: HeadphonesIcon, label: "24/7 online booking" },
            ].map((f) => (
              <div key={f.label} className="flex items-center gap-3 p-4 rounded-lg border border-border bg-card">
                <f.icon className="text-primary shrink-0" size={18} strokeWidth={1.5} />
                <span className="text-sm" dangerouslySetInnerHTML={{ __html: f.label }} />
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button asChild size="lg" className="h-12 px-8" data-testid="comparison-cta">
              <Link to="/quote">Try our instant quote <ArrowRight size={16} className="ml-2" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — 4 steps */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">How it works</span>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-3">Book your crossing in four steps.</h2>
          <p className="text-muted-foreground mt-6 leading-relaxed">
            Our straightforward four-step process makes booking UK to France transport easy and stress-free.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {[
            { n: "1", title: "Register your pet", body: "Create your free account and add your pet's details: breed, age, and any special requirements." },
            { n: "2", title: "Book your crossing", body: "Select pickup anywhere in the UK, a destination in Europe, and your preferred date." },
            { n: "3", title: "We collect your pet", body: "Our professional driver arrives at your door at the scheduled time with a secure, comfortable carrier." },
            { n: "4", title: "Safe delivery", body: "Your pet arrives happy and relaxed at the destination. Real-time updates throughout the journey." },
          ].map((s) => (
            <Card key={s.n} className="p-7 border-border bg-card h-full">
              <span className="inline-flex h-10 w-10 rounded-full bg-primary/15 text-primary font-display text-lg items-center justify-center">
                {s.n}
              </span>
              <h3 className="font-display text-lg mt-5 font-medium">{s.title}</h3>
              <p className="text-muted-foreground text-sm mt-3 leading-relaxed">{s.body}</p>
            </Card>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap gap-3">
          <Button asChild><Link to="/book">Register &amp; book</Link></Button>
          <Button asChild variant="outline"><Link to="/quote">Get a free quote</Link></Button>
        </div>
      </section>

      {/* UK CITIES GRID — text-only */}
      <section className="bg-secondary/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Door-to-door collection</span>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight mt-3 leading-tight">
              Pet transport from major UK cities to France.
            </h2>
            <p className="text-muted-foreground mt-6 leading-relaxed">
              DEFRA Type 2 authorised service with door-to-door collection from your home, safe travel via Eurotunnel,
              and delivery anywhere in France, Belgium, Netherlands, Germany, Spain and beyond.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {[
              "London", "Manchester", "Birmingham", "Leeds", "Edinburgh",
              "Glasgow", "Bristol", "Liverpool", "Newcastle", "Sheffield",
              "Nottingham", "Cardiff",
            ].map((city) => (
              <Link
                key={city}
                to={`/blog/pet-transport-${city.toLowerCase()}`}
                className="group rounded-lg border border-border bg-card px-4 py-3 text-sm hover:border-accent/60 hover:text-accent transition"
                data-testid={`home-city-${city.toLowerCase()}`}
              >
                {city} → France
              </Link>
            ))}
          </div>
          <div className="mt-10">
            <Button asChild variant="outline"><Link to="/routes">View all collection areas <ArrowRight size={14} className="ml-2" /></Link></Button>
          </div>
        </div>
      </section>

      {/* FAQ — visible on page, matches FAQ JSON-LD */}
      <section className="bg-secondary/40 border-y border-border" id="faq">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Common questions</span>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-3">Frequently asked questions.</h2>
          <p className="text-muted-foreground mt-6 max-w-2xl leading-relaxed">
            Everything you need to know about UK → Europe pet transport, Eurotunnel LeShuttle Pet, pet passports after Brexit, and our instant pricing.
          </p>
          <div className="mt-12 space-y-4" data-testid="home-faq-list">
            {[
              { q: "How much is a pet taxi from London to Paris?", a: "Our instant quote calculator gives you the exact price in under 60 seconds. London to Paris pet transport starts at £826 for a Volkswagen Passat, excluding the Eurotunnel LeShuttle Pet crossing fee (approximately £130–£250)." },
              { q: "Do you transport cats and ferrets as well as dogs?", a: "Yes — dogs, cats, and ferrets are all transported under the standard EU Pet Passport / GB Animal Health Certificate rules. Rabbits and small pets can also be arranged with slightly different paperwork." },
              { q: "Is this a pet taxi or air cargo?", a: "This is a professional pet taxi service — your pet travels in a climate-controlled Mercedes or Volkswagen Passat with a certified handler, door-to-door. No cages, no cargo hold, no Animal Reception Centre layovers." },
              { q: "Can I travel with my pet in the same vehicle?", a: "Yes, passengers are welcome in all vehicles at no extra charge, subject to seat count (Passat/E-Class: 1–3; Mercedes V-Class: up to 6). You and your pet door-to-door." },
              { q: "How quickly do I get a quote?", a: "Immediately — our online calculator at /quote returns the exact driving distance and price for all three vehicles in under 60 seconds. No form, no email required, no callbacks." },
              { q: "Are you DEFRA approved?", a: "Yes. We hold DEFRA Type 2 transporter authorisation, the regulatory tier required for commercial cross-border pet transport in the EU." },
            ].map((f, i) => (
              <details
                key={i}
                className="group rounded-lg border border-border bg-card p-6 open:border-accent/60 transition"
                data-testid={`home-faq-${i}`}
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <h3 className="font-display text-lg pr-6">{f.q}</h3>
                  <span className="shrink-0 inline-flex h-7 w-7 rounded-full border border-border items-center justify-center text-xs transition group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="text-muted-foreground mt-4 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild><Link to="/quote">Get a free instant quote</Link></Button>
            <Button asChild variant="outline"><Link to="/blog/complete-uk-europe-pet-transport-guide">Read the complete guide</Link></Button>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 text-center">
        <span className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Get started</span>
        <h2 className="font-display text-4xl md:text-5xl tracking-tight mt-4">
          Ready to transport your pet?
        </h2>
        <p className="text-muted-foreground mt-6 leading-relaxed max-w-2xl mx-auto">
          Join thousands of satisfied customers who trust us for safe, comfortable transport between the UK and
          Europe via Eurotunnel.
        </p>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Button asChild size="lg" className="h-12 px-8" data-testid="final-book-cta">
            <Link to="/book">Book your crossing</Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="h-12 px-8" data-testid="final-quote-cta">
            <Link to="/quote">Get a free quote</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
